import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USER_ROLES, ROLE_LABELS, type UserOutput, type InviteUserInput, type UpdateUserInput } from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError, isHttpStatus } from '../lib/http-error.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { ForbiddenPage } from '../components/ForbiddenPage.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { EditIcon } from '../components/ActionIcons.js';
import { fmtDateTime } from '../lib/date.js';

export function TeamsPage() {
  const { api, user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<UserOutput | null>(null);
  const [deleting, setDeleting] = useState<UserOutput | null>(null);
  const [resettingPassword, setResettingPassword] = useState<UserOutput | null>(null);

  const list = useQuery({
    queryKey: ['users'],
    enabled: api !== null,
    queryFn: async () => {
      if (!api) throw new Error('not authenticated');
      const res = await fetch('/v1/users?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: UserOutput[]; total: number } };
      return body.data;
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: UpdateUserInput }) => {
      const res = await fetch(`/v1/users/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditing(null);
    },
  });

  const invite = useMutation({
    mutationFn: async (input: InviteUserInput) => {
      const res = await fetch('/v1/users/invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowNew(false);
    },
  });

  const resendInvite = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/users/${id}/resend-invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const resetPw = useMutation({
    mutationFn: async ({ id, newPassword }: { id: string; newPassword: string }) => {
      const res = await fetch(`/v1/users/${id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify({ newPassword }),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setResettingPassword(null);
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleting(null);
    },
  });

  const { canDo } = useCapabilities();
  const canManage = canDo('users.manage');

  if (list.error && isHttpStatus(list.error, 403)) {
    return <ForbiddenPage title="No access" message="The team directory is only visible to CEO and GM roles." />;
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Team</h1>
          <p className="text-sm text-muted">{list.data?.items.filter((u) => u.status !== "DISABLED").length ?? '—'} members</p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowNew(true)}
            className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
          >
            + New member
          </button>
        )}
      </header>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Last login</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <TableSkeletonRows colSpan={6} />}
            {list.error && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>
            )}
            {list.data?.items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted">No team members found.</td></tr>
            )}
            {list.data?.items.filter((u) => u.status !== "DISABLED").map((u) => (
              <tr key={u.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                <td className="px-4 py-3 text-white">{u.displayName}</td>
                <td className="px-4 py-3 text-muted">{u.email}</td>
                <td className="px-4 py-3 text-muted">{ROLE_LABELS[u.role] ?? u.role}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${u.status === 'ACTIVE' ? 'pill-success' : 'pill-danger'}`}>
                    {u.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted">{fmtDateTime(u.lastLoginAt)}</td>
                <td className="px-4 py-3 text-right">
                  {canManage && u.id !== currentUser?.id && (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <EditIcon onClick={() => setEditing(u)} label="Edit user" />
                      <button onClick={() => setResettingPassword(u)} className="text-xs btn-ghost-accent border px-2 py-1 rounded">Reset password</button>
                      {u.status === 'INVITED' && (
                        <button onClick={() => resendInvite.mutate(u.id)} className="text-xs btn-ghost-accent border px-2 py-1 rounded">Resend</button>
                      )}
                      <button onClick={() => setDeleting(u)} className="text-xs btn-ghost-danger border px-2 py-1 rounded">Disable</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewUserModal onClose={() => setShowNew(false)} onSubmit={invite.mutate} error={invite.error?.message ?? null} submitting={invite.isPending} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onSubmit={(patch) => update.mutate({ id: editing.id, patch })} error={update.error?.message ?? null} submitting={update.isPending} />}
      {resettingPassword && (
        <ResetPasswordModal
          user={resettingPassword}
          onClose={() => setResettingPassword(null)}
          onSubmit={(newPassword) => resetPw.mutate({ id: resettingPassword.id, newPassword })}
          error={resetPw.error?.message ?? null}
          submitting={resetPw.isPending}
        />
      )}
      {deleting && (
        <ConfirmModal
          title="Disable user"
          message={`Disable ${deleting.displayName}? They will no longer be able to log in.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting.id)}
          error={remove.error?.message ?? null}
          submitting={remove.isPending}
          confirmText="Disable"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}
    </div>
  );
}

function NewUserModal({ onClose, onSubmit, error, submitting }: {
  onClose: () => void;
  onSubmit: (input: InviteUserInput) => void;
  error: string | null;
  submitting: boolean;
}) {
  const [form, setForm] = useState<InviteUserInput>({
    email: '',
    displayName: '',
    role: 'Staff',
  });

  function update<K extends keyof InviteUserInput>(k: K, v: InviteUserInput[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <ModalShell title="Invite team member" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); if (!form.email || !form.displayName || !form.role) return; onSubmit(form); }} className="space-y-3">
        <div>
          <label className="block text-xs text-muted mb-1">Email *</label>
          <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" required />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Display name *</label>
          <input value={form.displayName} onChange={(e) => update('displayName', e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" required />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Role *</label>
          <select value={form.role} onChange={(e) => update('role', e.target.value as InviteUserInput['role'])} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" required>
            {USER_ROLES.filter((r) => r !== 'CLIENT').map((r) => <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>)}
          </select>
        </div>
        {error && <p className="text-xs text-rose-400">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button type="submit" disabled={submitting || !form.email || !form.displayName || !form.role} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">{submitting ? 'Inviting…' : 'Send invite'}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function EditUserModal({ user, onClose, onSubmit, error, submitting }: {
  user: UserOutput;
  onClose: () => void;
  onSubmit: (patch: UpdateUserInput) => void;
  error: string | null;
  submitting: boolean;
}) {
  const [form, setForm] = useState<UpdateUserInput>({
    displayName: user.displayName,
    role: user.role,
    status: user.status,
  });

  function update<K extends keyof UpdateUserInput>(k: K, v: UpdateUserInput[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <ModalShell title="Edit team member" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-3">
        <div>
          <label className="block text-xs text-muted mb-1">Display name</label>
          <input value={form.displayName ?? ''} onChange={(e) => update('displayName', e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-muted mb-1">Role</label>
            <select value={form.role ?? ''} onChange={(e) => update('role', e.target.value as UpdateUserInput['role'])} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent">
              <option value="">—</option>
              {USER_ROLES.filter((r) => r !== 'CLIENT').map((r) => <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Status</label>
            <select value={form.status ?? ''} onChange={(e) => update('status', e.target.value as UpdateUserInput['status'])} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent">
              <option value="">—</option>
              <option value="ACTIVE">ACTIVE</option>
              <option value="INVITED">INVITED</option>
              <option value="DISABLED">DISABLED</option>
            </select>
          </div>
        </div>
        {error && <p className="text-xs text-rose-400">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button type="submit" disabled={submitting} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">{submitting ? 'Saving…' : 'Save changes'}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function ResetPasswordModal({ user, onClose, onSubmit, error, submitting }: {
  user: UserOutput;
  onClose: () => void;
  onSubmit: (newPassword: string) => void;
  error: string | null;
  submitting: boolean;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [validation, setValidation] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidation(null);
    if (password.length < 12) {
      setValidation('Password must be at least 12 characters');
      return;
    }
    if (password !== confirm) {
      setValidation('Passwords do not match');
      return;
    }
    onSubmit(password);
  };

  return (
    <ModalShell title={`Reset password for ${user.displayName}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <p className="text-xs text-muted">Set a new password for {user.email}. The user will be able to change it after signing in.</p>
        <div>
          <label className="block text-xs text-muted mb-1">New password</label>
          <div className="relative">
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white pr-14"
              minLength={12}
              required
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-white text-xs"
              tabIndex={-1}
            >
              {show ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Confirm new password</label>
          <input
            type={show ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
            required
          />
        </div>
        {(error || validation) && <p className="text-xs text-rose-400">{error || validation}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button type="submit" disabled={submitting} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">{submitting ? 'Saving…' : 'Reset password'}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-lg mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, onClose, onConfirm, error, submitting, confirmText, confirmClass }: {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  error: string | null;
  submitting: boolean;
  confirmText: string;
  confirmClass: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto mx-4 shadow-2xl animate-pop p-5">
        <h2 className="text-base font-semibold text-white mb-2">{title}</h2>
        <p className="text-sm text-muted mb-4">{message}</p>
        {error && <p className="text-xs text-rose-400 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button onClick={onConfirm} disabled={submitting} className={`text-sm font-medium px-4 py-1.5 rounded-lg disabled:opacity-50 ${confirmClass}`}>{submitting ? 'Working…' : confirmText}</button>
        </div>
      </div>
    </div>
  );
}
